import {
  DokumentQuelldaten,
  DokumentVersandArt,
  DokumentVorschau,
} from '@core/domain/src/models/Dokument';
import { VariantProps } from 'class-variance-authority';
import { useState } from 'react';
import { toast } from 'sonner';
import { DokumentVorschauDialog } from './DokumentVorschauDialog';
import { VersandArtDialog } from './VersandArtDialog';
import { useDokumentVorschau } from '../../adapter/opm-cockpit-backend/useDokumentVorschau';
import { Button, buttonVariants } from '../../components/ui/button';

type DokumentVorschauButtonProps = Omit<
  Partial<
    React.ComponentProps<'button'> &
      VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
      }
  >,
  'children'
> & {
  dokumentQuelldaten: DokumentQuelldaten | null;
  versandArt: DokumentVersandArt | null;
};

export const DokumentVorschauButton: React.FC<DokumentVorschauButtonProps> = (
  props,
) => {
  const dokumentVorschauMutation = useDokumentVorschau();
  const [preview, setPreview] = useState<DokumentVorschau | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isVersandArtDialogOpen, setIsVersandArtDialogOpen] = useState(false);
  const [selectedVersandArt, setSelectedVersandArt] = useState<
    DokumentVersandArt | ''
  >(
    props.versandArt != null
      ? (parseInt(props.versandArt.toString(), 10) as DokumentVersandArt)
      : '',
  );

  const triggerPreview = (payload: { versandArt: DokumentVersandArt }) => {
    dokumentVorschauMutation.mutate(
      {
        dokumentQuelldaten: props.dokumentQuelldaten,
        versandArt: payload.versandArt,
      },
      {
        onSuccess: (result) => {
          setPreview(result);
          setIsPreviewDialogOpen(true);
        },
        onError: (err) => {
          const message =
            err instanceof Error
              ? err.message
              : 'Fehler beim Laden der Dokumentvorschau.';
          toast.error(message);
        },
      },
    );
  };

  const handlePreview = () => {
    setIsVersandArtDialogOpen(true);
  };

  const handleConfirmVersandArt = () => {
    if (!props.dokumentQuelldaten || selectedVersandArt === '') {
      return;
    }

    triggerPreview({
      versandArt: selectedVersandArt,
    });
    setIsVersandArtDialogOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-2"
        {...props}
        onClick={handlePreview}
        disabled={
          dokumentVorschauMutation.isPending ||
          !props.dokumentQuelldaten ||
          props.disabled
        }
      >
        {dokumentVorschauMutation.isPending
          ? 'Vorschau wird geladen...'
          : 'Vorschau anzeigen'}
      </Button>

      <VersandArtDialog
        open={isVersandArtDialogOpen}
        selectedVersandArt={selectedVersandArt}
        isLoading={dokumentVorschauMutation.isPending}
        onOpenChange={setIsVersandArtDialogOpen}
        onChange={setSelectedVersandArt}
        onConfirm={handleConfirmVersandArt}
      />

      <DokumentVorschauDialog
        open={isPreviewDialogOpen}
        onOpenChange={setIsPreviewDialogOpen}
        preview={preview}
      />
    </>
  );
};
